import { useCallback, useEffect, useRef, useState, type ComponentProps } from 'react'

import { XIcon } from 'lucide-react'
import type { Control, FieldValues, UseControllerProps } from 'react-hook-form'

import { Icon } from '@/components/icon'
import { Render } from '@/components/render-if'
import { Button } from '@/components/ui'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Command, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command'
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { useDebouncedCallback } from '@/hooks/use-debounce-callback'
import { cn } from '@/infra'

type TAutoCompleteProps<T> = {
  data: T[]
  value: T
  onSelect: (value: T | T[keyof T]) => void
  onSearch: (query: string) => void
  onEndReach?: VoidFunction
  isLoading?: boolean
  isLoadingMore?: boolean
  isError?: boolean
  labelKey: keyof T
  valueKey: keyof T
  placeholder?: string
  searchPlaceholder?: string
  errorMessage?: string
  emptyMessage?: string
  retry?: VoidFunction
  className?: ComponentProps<typeof Button>['className']
  contentClassName?: ComponentProps<typeof PopoverContent>['className']
  container?: HTMLElement | null
  showClearButton?: boolean
  disabled?: boolean
  onClear?: () => void
}

const AutoComplete = <T extends Record<string, unknown>>({
  data,
  isLoading,
  isLoadingMore,
  isError,
  labelKey,
  className,
  contentClassName,
  retry,
  value,
  valueKey,
  onSelect,
  onSearch,
  onEndReach,
  placeholder = 'Selecionar',
  searchPlaceholder = 'Buscar',
  errorMessage = 'Não foi possível carregar os dados',
  emptyMessage = 'Nenhum resultado encontrado',
  showClearButton = false,
  container,
  onClear,
  ...props
}: TAutoCompleteProps<T>) => {
  const [open, setOpen] = useState(false)
  const listRef = useRef<HTMLDivElement>(null)
  const onSearchDebounced = useDebouncedCallback(onSearch, 500)
  const displayName = value?.[labelKey] ? value[labelKey] : placeholder

  const handleCloseSelection = () => {
    setOpen(false)
    onSearch(' ')
  }

  const handleClear = (event: React.MouseEvent) => {
    event.stopPropagation()
    onClear?.()
    onSelect?.({} as T)
  }

  const handleSelectResult = (product: T) => {
    onSelect?.(product)
    handleCloseSelection()
  }

  const handleScroll = useCallback(() => {
    if (!listRef.current || !onEndReach) return

    const { scrollTop, scrollHeight, clientHeight } = listRef.current
    const THRESHOLD_BASE = 20
    if (scrollHeight - scrollTop - clientHeight < THRESHOLD_BASE && !isLoadingMore) {
      onEndReach?.()
    }
  }, [onEndReach, isLoadingMore])

  useEffect(() => {
    if (open) {
      onSearch(' ')
    }
  }, [open, onSearch])

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          aria-expanded={open}
          data-opened={String(open)}
          data-selected={String(!!value?.[labelKey])}
          className={cn(
            'border-input truncate bg-neutral-50 px-3 text-base font-normal text-neutral-500 shadow-none hover:text-neutral-600',
            'focus-visible:ring-primary outline-offset-0 outline-none',
            'aria-invalid:border-destructive',
            'data-[opened=true]:border-primary data-[opened=true]:border',
            '[&:not(:disabled)[data-selected=true]]:bg-slate-100 [&:not(:disabled)[data-selected=true]]:text-gray-800',
            'disabled:border-neutral-200 disabled:bg-neutral-100 disabled:text-neutral-500 disabled:opacity-100',
            'h-10 w-96 justify-between',
            className,
          )}
          {...props}
        >
          <div className="flex w-full items-center justify-between gap-2 pr-0">
            <span className="truncate">{String(displayName)}</span>
            <div className="flex items-center gap-2">
              <Icon
                name={open ? 'chevron-up' : 'chevron-down'}
                size={16}
                className="text-muted-foreground/80 shrink-0"
              />

              {showClearButton && !!value?.[labelKey] && !props.disabled && (
                <span
                  role="none"
                  onClick={handleClear}
                  className={cn(
                    'flex h-6 w-6 items-center justify-center rounded-sm',
                    'text-muted-foreground/80 hover:bg-background',
                    'transition-[color,box-shadow] outline-none',
                  )}
                >
                  <XIcon size={24} aria-hidden="true" />
                </span>
              )}
            </div>
          </div>
        </Button>
      </PopoverTrigger>

      <PopoverContent
        side="bottom"
        container={container}
        className={cn(
          'border-input w-full max-w-(--radix-popper-anchor-width) min-w-(--radix-popper-anchor-width) p-0',
          contentClassName,
        )}
      >
        <Command shouldFilter={false}>
          <CommandInput
            onValueChange={(_value) => onSearchDebounced(_value || ' ')}
            placeholder={searchPlaceholder}
            isSearching={isLoading}
          />
          <CommandList ref={listRef} onScroll={handleScroll}>
            <Render.If condition={isLoading}>{() => <DefaultLoadingSkeleton />}</Render.If>
            <Render.If condition={!isError && !isLoading && !data?.length}>
              {() => <div className="p-4 text-sm">{emptyMessage}</div>}
            </Render.If>
            <Render.If condition={isError}>
              {() => (
                <div className="p-2">
                  <Alert className="flex items-center justify-between px-4 py-1" variant="destructive">
                    <AlertDescription>{errorMessage}</AlertDescription>
                    <Render.If condition={retry}>
                      {(retryFn) => (
                        <Button className="hover:bg-red-100" variant="ghost" onClick={retryFn}>
                          <Icon name="refresh-ccw" className="h-4 w-4" />
                        </Button>
                      )}
                    </Render.If>
                  </Alert>
                </div>
              )}
            </Render.If>
            <Render.If condition={data}>
              {(_data) =>
                _data.map((item) => (
                  <CommandItem
                    onSelect={() => handleSelectResult(item)}
                    key={String(item?.[valueKey])}
                    value={String(item?.[labelKey])}
                  >
                    <Icon
                      name="check"
                      className={cn('mr-2 h-4 w-4', value?.[labelKey] === item[labelKey] ? 'opacity-100' : 'opacity-0')}
                    />
                    <p style={{ overflowWrap: 'anywhere' }} className="w-full">
                      {String(item[labelKey])}
                    </p>
                  </CommandItem>
                ))
              }
            </Render.If>
            {isLoadingMore && (
              <div className="flex justify-center py-2">
                <Icon name="loader" className="text-muted-foreground h-4 w-4 animate-spin" />
              </div>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

function DefaultLoadingSkeleton() {
  return (
    <CommandGroup>
      {[1, 2, 3].map((i) => (
        <CommandItem key={i} disabled>
          <div className="bg-muted-foreground/25 h-6 w-full animate-pulse rounded" />
        </CommandItem>
      ))}
    </CommandGroup>
  )
}

type TAutoCompleteFieldProps<
  TOption,
  TFieldValues extends FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
> = Omit<TAutoCompleteProps<TOption>, 'value' | 'onSelect'> &
  Omit<UseControllerProps<TFieldValues>, 'control'> & {
    control?: Control<TFieldValues, TContext, TTransformedValues>
    label: string
    onSelect?: (value: TOption | TOption[keyof TOption]) => void
    onClear?: () => void
    errorSize?: 'sm' | 'md' | 'lg'
  }

const AutoCompleteField = <
  TOption extends Record<string, unknown>,
  TFieldValues extends FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>({
  control,
  name,
  label,
  errorSize,
  ...props
}: TAutoCompleteFieldProps<TOption, TFieldValues, TContext, TTransformedValues>) => {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel aria-disabled={props.disabled}>{label}</FormLabel>
          <FormControl>
            <AutoComplete
              {...props}
              {...field}
              data={props.data}
              value={field.value}
              onSelect={(value) => {
                props?.onSelect?.(value)
                field.onChange(value)
              }}
            />
          </FormControl>
          <FormMessage size={errorSize} />
        </FormItem>
      )}
    />
  )
}

export { AutoComplete, AutoCompleteField }
